'use client';
import { EyeFilled, HeartFilled, HeartOutlined } from '@ant-design/icons';
import { SailBoatIcon } from '@marine/icons';
import { Avatar, AvatarProps, Button, Card, Typography } from 'antd';
import cn from 'classnames';
import { ReactNode } from 'react';
import { ReactCountryFlag } from 'react-country-flag';
import { Image } from 'sailor-ui';
import styles from './public-boat-card.module.scss';

const { Paragraph, Title } = Typography;

export type PublicBoatCardProps = {
  /**
   * Boat name to be rendered as card title.
   */
  title: string;
  /**
   * Optional className to style card Wrapper.
   */
  className?: string;
  /**
   * Views count.
   */
  views?: number;
  /**
   * favorites count.
   */
  favorites_count?: number;
  /**
   * Optional config to remove shadow (Default true).
   */
  hasShadow?: boolean;
  /**
   * URL for boat image or conditionally render ReactElement.
   */
  boatImage?: string;
  /**
   * Optional Flag, just include Country code to render Flag, ex: 'US' | 'Gb' | 'de';
   */
  flag?: string;
  /**
   * Optional Avatar with AntD Avatar props: { name: 'Initials' } | { src: 'URL', name: 'Shows as tooltip when image is provided' };
   */
  avatar?: AvatarProps & { name?: ReactNode };
  /**
   * Optional onClick callback for when clicking the card.
   */
  onClick?: () => void;
  /**
   * Optional onClickLike callback for when clicking the Like button.
   */
  onFavoriteClick?: () => void;

  /**
   * Favorite toggle button state
   */
  disableFavoriteButton?: boolean;
  /**
   * Optional isFavorite to show if is liked. (default: false)
   */
  isFavorite?: boolean;
  /**
   * Optional Boat model or description with ellipsis.
   */
  boatModel?: string;

  showFavoriteToggle?: boolean;

  boatId?: string;

  imageComponent?: React.ReactElement;
};

export const PublicBoatCard = ({
  className,
  title,
  isFavorite = false,
  onClick,
  boatImage,
  flag,
  hasShadow = true,
  favorites_count,
  onFavoriteClick,
  disableFavoriteButton = false,
  avatar,
  views,
  boatModel,
  showFavoriteToggle = false,
  imageComponent,
  boatId,
  ...rest
}: PublicBoatCardProps) => {
  const cover = (
    <div className={styles.cover}>
      {imageComponent || <Image src={boatImage} format="card" alt={title} />}
      <div
        className={cn(styles.stats, {
          [styles.statsShadow]:
            (hasShadow && boatImage && (views !== null || favorites_count !== null)) || (hasShadow && avatar),
        })}
      >
        {views ? (
          <div className={styles.statsItem}>
            <EyeFilled />
            <p>{views.toLocaleString()}</p>
          </div>
        ) : null}

        {favorites_count ? (
          <div className={styles.statsItem}>
            <HeartFilled />
            <p>{favorites_count.toLocaleString()}</p>
          </div>
        ) : null}

        {avatar && (
          <div className={cn(styles.statsItem, styles.avatar)}>
            <Avatar {...avatar}>{avatar?.name || avatar?.children}</Avatar>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div
      className={`${styles.cardWrapper} ${className || ''}`}
      onClick={onClick}
      onKeyDown={() => {}}
      role="button"
      tabIndex={0}
      data-cy="public-boat-card"
      data-boat-id={boatId}
    >
      {showFavoriteToggle && (typeof onFavoriteClick === 'function' || isFavorite) && (
        <Button
          icon={isFavorite ? <HeartFilled /> : <HeartOutlined />}
          shape="circle"
          className={styles.like}
          /*
           * These cards are wrapped in a link, and the page-transition bar hooks anchor clicks. Its
           * handler sits on the anchor, while React runs this `onClick` at the root — so by the time
           * the click is cancelled below, the bar has already started for a navigation that will
           * never happen, and nothing ever stops it. This is the bar's own opt-out: it is read off
           * the clicked element or any ancestor below the anchor, so it covers the icon inside too.
           */
          data-prevent-progress="true"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();

            if (typeof onFavoriteClick === 'function') {
              onFavoriteClick();
            }
          }}
          disabled={disableFavoriteButton}
        />
      )}

      <Card className={styles.card} hoverable variant="borderless" cover={cover} {...rest}>
        <div className={styles.titleContainer}>
          <div className={styles.title}>
            <Title level={3}>{title}</Title>
          </div>
          {flag && (
            <div className={styles.flag}>
              <ReactCountryFlag
                countryCode={flag}
                svg
                style={{
                  height: 20,
                  width: 'auto',
                  borderRadius: 3,
                }}
                title={flag}
              />
            </div>
          )}
        </div>
        {boatModel && (
          <div className={styles.boatModel}>
            <div className={styles.boatModelIcon}>
              <SailBoatIcon />
            </div>
            <div className={styles.boatModelTitle}>
              <Paragraph ellipsis={{ tooltip: boatModel }}>{boatModel}</Paragraph>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};
